import { Table, TableColumnsType } from 'antd'
import { IOrder, IOrderItem } from '../../../../../types/order'
import { Box, Typography } from '@mui/material'
import { numberFormat } from '../../../../../utils'
import { sumBy } from 'lodash'
import Pagination from '../../../../../components/Pagination'
import { Link } from 'react-router-dom'
import PageUrls from '../../../../../constants/page-urls'
import dayjs from 'dayjs'
import { ONLY_DATE_FORMAT } from '../../../../../constants/global'

interface Props {
  orders: IOrder[]
  page: number
  perPage: number
  setOrderFilters: React.Dispatch<
    React.SetStateAction<{ page: number; perPage: number }>
  >
  total: number
}

const OrdersTable = (props: Props) => {
  const { orders, page, perPage, setOrderFilters, total } = props

  const expandedRowRender = (record: any) => {
    const columns = [
      {
        title: 'ชื่อสินค้า',
        dataIndex: 'productName',
        key: 'productName',

        align: 'left' as const,
        render: (productName: string) => (
          <Typography variant="subtitle2" color="text.secondary">
            {productName}
          </Typography>
        ),
      },
      {
        title: 'ขนาด',
        dataIndex: 'size',
        key: 'size',
        align: 'center' as const,
        width: 120,
        render: (_: string, item: IOrderItem) => {
          return (
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textAlign={'center'}
            >
              {item?.meta?.size ?? '-'}
            </Typography>
          )
        },
      },
      {
        title: 'น้ำหนัก',
        dataIndex: 'weight',
        key: 'weight',
        width: 120,
        align: 'center' as const,
        render: (_: string, item: IOrderItem) => {
          return (
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textAlign={'center'}
            >
              {item?.meta?.weight ?? '-'}
            </Typography>
          )
        },
      },
      {
        title: 'สี',
        dataIndex: 'color',
        key: 'color',
        width: 120,
        align: 'center' as const,
        render: (_: string, item: IOrderItem) => {
          return (
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textAlign={'center'}
            >
              {item?.meta?.color ?? '-'}
            </Typography>
          )
        },
      },
      {
        title: 'จำนวน',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 120,
        align: 'center' as const,
        render: (quantity: number) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {numberFormat(quantity)}
          </Typography>
        ),
      },
      {
        title: 'ราคา',
        dataIndex: 'total',
        key: 'total',
        width: 120,
        align: 'center' as const,
        render: (total: number) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {numberFormat(total)}
          </Typography>
        ),
      },
    ]
    return (
      <Table
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={record.items}
        pagination={false}
      />
    )
  }

  const columns: TableColumnsType<IOrder & { key: string; numberRow: number }> =
    [
      {
        title: 'ลำดับ',
        dataIndex: 'numberRow',
        key: 'numberRow',
        align: 'center' as const,
        width: 80,
        render: (numberRow: number) => {
          return (
            <Box>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                textAlign={'center'}
              >
                {numberRow}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'เลขออเดอร์',
        dataIndex: 'code',
        key: 'code',
        width: 150,
        align: 'center' as const,
        render: (code: string, order: IOrder) => (
          <>
            <Typography
              variant="subtitle1"
              color="text.primary"
              component={Link}
              target='_blank'
              to={`${PageUrls.Orders.Detail}/${order.objectId}`}
              sx={{ cursor: 'pointer' }}
            >
              {code}
            </Typography>
            <Typography fontSize={11}>
              {order.orderDate
                ? dayjs(order.orderDate).format(ONLY_DATE_FORMAT)
                : ''}
            </Typography>
          </>
        ),
      },
      {
        title: 'ข้อมูลลูกค้า',
        dataIndex: 'customer',
        key: 'customer',
        align: 'left' as const,
        render: (_: number, order: IOrder) => <>{order.recipient.name}</>,
      },
      {
        title: 'จำนวน',
        dataIndex: 'quantity',
        key: 'quantity',
        align: 'center' as const,
        width: 100,
        render: (_: number, order: IOrder) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {numberFormat(sumBy(order.items, 'quantity'))}
          </Typography>
        ),
      },
      {
        title: 'COD',
        dataIndex: 'codTotal',
        key: 'codTotal',
        width: 100,
        align: 'center' as const,
        render: (_: number, order: IOrder) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {numberFormat(order.paymentMethod === 'COD' ? order.total : 0, 2)}
          </Typography>
        ),
      },
      {
        title: 'BANK',
        dataIndex: 'bankTotal',
        key: 'bankTotal',
        width: 100,
        align: 'center' as const,
        render: (_: number, order: IOrder) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {numberFormat(order.paymentMethod !== 'COD' ? order.total : 0, 2)}
          </Typography>
        ),
      },
    ]

  return (
    <>
      <Table
        size={'middle'}
        columns={columns as any}
        expandable={{
          expandedRowRender: expandedRowRender as any,
          rowExpandable: (record) => true,
        }}
        dataSource={orders?.map((order: IOrder, index: number) => {
          return {
            ...order,
            key: index,
            numberRow: index + 1 + ((page || 0) - 1) * (perPage || 0),
          }
        })}
        bordered
        pagination={false}
      ></Table>
      <Pagination
        total={total}
        current={page!}
        pageSize={perPage!}
        showSizeChanger={false}
        onChange={(page, perPage) => setOrderFilters({ page, perPage })}
      />
    </>
  )
}

export default OrdersTable
